import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { FlashRepository } from 'repository/flashRepository';
import { PlacePickerModal } from 'components/map/PlacePickerModal';
import {
    AppScreen, Badge, Button, InfoGrid, Section, SectionHeader, TextField,
} from 'components/ds';
import styles from './Flash.module.css';

/**
 * 급번개 투표 화면 — 공유 링크로 접근. 비회원 가능.
 * URL: /flash/vote/:token
 */

interface Candidate {
    candidateId: string;
    placeName: string;
    placeAddress?: string;
    latitude?: number;
    longitude?: number;
    externalPlaceId?: string;
    imageUrl?: string;
    addedByName?: string;
    voteCount: number;
    voters: string[];
}

interface FlashData {
    flashId: string;
    shareToken: string;
    creatorNickname: string;
    title: string;
    memo?: string;
    startDatetime: string;
    endDatetime: string;
    voteDeadline: string;
    status: 'VOTING' | 'DECIDED' | 'CANCELLED' | 'EXPIRED';
    decidedCandidateId?: string;
    decidedMeetingId?: string;
    candidates: Candidate[];
    totalVotes: number;
}

const VOTER_TOKEN_KEY = 'flash_voter_token';
const VOTER_NAME_KEY = 'flash_voter_name';

const getOrCreateVoterToken = (): string => {
    let token = localStorage.getItem(VOTER_TOKEN_KEY);
    if (!token) {
        token = 'v_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
        localStorage.setItem(VOTER_TOKEN_KEY, token);
    }
    return token;
};

const formatTime = (iso: string): string => {
    try {
        const d = new Date(iso);
        return d.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
};

const formatDateShort = (iso: string): string => {
    try {
        const d = new Date(iso);
        return d.toLocaleDateString('ko-KR', { month: 'long', day: 'numeric', weekday: 'short' });
    } catch { return ''; }
};

const safeDecode = (s?: string): string => {
    if (!s) return '';
    try { return decodeURIComponent(s); } catch { return s; }
};

// 마감 남은 시간 — 표시 문자열은 i18n 처리하므로 분/시간만 계산해서 반환
const computeRemaining = (deadlineIso: string): { ended: boolean; mins: number } => {
    const ms = new Date(deadlineIso).getTime() - Date.now();
    if (ms <= 0) return { ended: true, mins: 0 };
    return { ended: false, mins: Math.floor(ms / 60000) };
};

export const FlashVote = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [data, setData] = useState<FlashData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [voterName, setVoterName] = useState<string>(() => localStorage.getItem(VOTER_NAME_KEY) || '');
    const [showNameModal, setShowNameModal] = useState(false);
    const [nameInput, setNameInput] = useState('');
    const [pendingCandidateId, setPendingCandidateId] = useState<string | null>(null);
    const [isVoting, setIsVoting] = useState(false);
    const [toastMsg, setToastMsg] = useState('');
    const [showAddModal, setShowAddModal] = useState(false);
    const [isAddingPlace, setIsAddingPlace] = useState(false);
    const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const voterToken = useMemo(() => getOrCreateVoterToken(), []);

    const load = useCallback(async () => {
        if (!token) return;
        try {
            const res = await FlashRepository.onGetByToken(token);
            const raw = res?.data?.data || res?.data;
            // 응답이 없거나, 필수 필드가 없는 경우(만료/잘못된 토큰) 친화적 에러
            if (!raw || typeof raw !== 'object' || !raw.flashId) {
                throw new Error('not_found');
            }
            // candidates 가 누락되어도 후속 렌더가 깨지지 않도록 기본값
            const safe: FlashData = {
                ...raw,
                candidates: Array.isArray(raw.candidates) ? raw.candidates : [],
                totalVotes: typeof raw.totalVotes === 'number' ? raw.totalVotes : 0,
            };
            setData(safe);
            setError(null);
        } catch (e: any) {
            setData(null);
            setError(t('flash.vote.errorText'));
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => { load(); }, [load]);

    // 폴링 10초
    useEffect(() => {
        if (!data || data.status !== 'VOTING') return;
        pollingRef.current = setInterval(() => { load(); }, 10000);
        return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
    }, [data?.status, load]); // eslint-disable-line react-hooks/exhaustive-deps

    // 내 현재 투표 후보 추적 (voters 이름 매칭 — 인코딩 차이 대응)
    const myCandidateId = useMemo(() => {
        if (!data || !voterName) return null;
        const found = data.candidates.find(c =>
            c.voters.some(v => v === voterName || safeDecode(v) === voterName)
        );
        return found?.candidateId || null;
    }, [data, voterName]);

    const submitVote = async (candidateId: string, name: string) => {
        if (!token || isVoting) return;
        setIsVoting(true);
        try {
            await FlashRepository.onPostVote(token, {
                candidateId,
                voterName: name,
                voterToken,
            });
            setToastMsg(t('flash.vote.voteSuccess'));
            setTimeout(() => setToastMsg(''), 1800);
            await load();
        } catch (e: any) {
            setToastMsg(e?.response?.data?.message || t('flash.vote.voteFailed'));
            setTimeout(() => setToastMsg(''), 2500);
        } finally {
            setIsVoting(false);
        }
    };

    const handleVoteClick = (candidateId: string) => {
        if (!voterName) {
            setPendingCandidateId(candidateId);
            setShowNameModal(true);
            return;
        }
        submitVote(candidateId, voterName);
    };

    const handleConfirmName = () => {
        const n = nameInput.trim();
        if (!n) return;
        setVoterName(n);
        localStorage.setItem(VOTER_NAME_KEY, n);
        setShowNameModal(false);
        if (pendingCandidateId) {
            submitVote(pendingCandidateId, n);
            setPendingCandidateId(null);
        }
    };

    // handleAddPlace 는 PlacePickerModal 의 onSelect 안으로 인라인됨 (지도 선택 흐름 통합).


    if (loading) {
        return (
            <AppScreen bar="none">
                <p className={styles.state}>{t('flash.vote.loadingText')}</p>
            </AppScreen>
        );
    }
    if (error || !data) {
        return (
            <AppScreen bar="none">
                <p className={styles.state}>{error || t('flash.vote.errorText')}</p>
            </AppScreen>
        );
    }

    const isEnded = data.status !== 'VOTING' || new Date(data.voteDeadline).getTime() < Date.now();
    const sortedCandidates = [...data.candidates].sort((a, b) => b.voteCount - a.voteCount);
    const maxVotes = Math.max(1, ...data.candidates.map(c => c.voteCount));

    const deadlineText = data.status === 'DECIDED'
        ? t('flash.vote.decidedText')
        : data.status === 'CANCELLED'
            ? t('flash.vote.cancelledText')
            : (() => {
                const r = computeRemaining(data.voteDeadline);
                return r.ended
                    ? t('flash.vote.deadlineEnded')
                    : t('tone.homeFlash.activeDeadline', { minutes: r.mins });
            })();

    return (
        <AppScreen bar="none">
            {/* 무엇에 대한 투표인지 */}
            <div className={styles.intro}>
                <span className={styles.eyebrow}>{t('flash.vote.badge')}</span>
                <h1 className={styles.title}>{safeDecode(data.title)}</h1>
                <p className={styles.by}>
                    {t('flash.vote.creatorMessage', { creator: safeDecode(data.creatorNickname) })}
                </p>
            </div>

            <Section spacing="tight">
                <InfoGrid
                    items={[
                        {
                            label: t('flash.create.whenLabel'),
                            value: `${formatDateShort(data.startDatetime)} ${formatTime(data.startDatetime)}`,
                        },
                        { label: t('flash.create.deadlineLabel'), value: deadlineText },
                        ...(data.memo
                            ? [{ label: t('flash.create.memoLabel'), value: safeDecode(data.memo), full: true }]
                            : []),
                    ]}
                />
            </Section>

            {/* 후보 */}
            <Section divided>
                <SectionHeader
                    title={t('flash.vote.whereTitle')}
                    sub={t('flash.vote.totalVoters', { count: data.totalVotes })}
                />
                <ul className={styles.slots}>
                    {sortedCandidates.map((c, idx) => {
                        const isMyVote = myCandidateId === c.candidateId;
                        const isDecided = data.decidedCandidateId === c.candidateId;
                        const pct = (c.voteCount / maxVotes) * 100;
                        return (
                            <li key={c.candidateId} className={styles.slot}>
                                <div className={styles.slotTop}>
                                    <span className={styles.slotName}>{safeDecode(c.placeName)}</span>
                                    {isDecided && <Badge tone="brand">{t('flash.vote.decidedLabel')}</Badge>}
                                    {!isDecided && idx === 0 && c.voteCount > 0 && (
                                        <Badge tone="lime">{t('flash.vote.leading')}</Badge>
                                    )}
                                </div>

                                {c.placeAddress && (
                                    <span className={styles.slotAddr}>{safeDecode(c.placeAddress)}</span>
                                )}
                                {c.addedByName && idx > 0 && (
                                    <span className={styles.slotAddr}>
                                        {t('flash.vote.proposer', { name: safeDecode(c.addedByName) })}
                                    </span>
                                )}

                                <span className={styles.bar}>
                                    <span className={styles.barFill} style={{ width: `${pct}%` }} />
                                </span>

                                <div className={styles.slotTop}>
                                    <span className={styles.slotCount}>
                                        {t('flash.vote.voteCount', { count: c.voteCount })}
                                        {c.voters.length > 0
                                            ? ` · ${c.voters.slice(0, 3).map(safeDecode).join(', ')}${c.voters.length > 3
                                                ? t('flash.status.voterMore', { count: c.voters.length - 3 })
                                                : ''}`
                                            : ''}
                                    </span>
                                </div>

                                <div className={styles.slotAction}>
                                    <Button
                                        variant={isMyVote ? 'secondary' : 'primary'}
                                        size="sm"
                                        block
                                        onClick={() => { if (!isEnded) handleVoteClick(c.candidateId); }}
                                        disabled={isEnded || isVoting}
                                    >
                                        {isEnded
                                            ? (isMyVote ? t('flash.vote.voted') : t('flash.vote.deadlineEnded'))
                                            : (isMyVote ? t('flash.vote.voted') : t('flash.vote.voteHere'))}
                                    </Button>
                                </div>
                            </li>
                        );
                    })}
                </ul>

                {!isEnded && data.candidates.length < 5 && (
                    <div className={styles.addAction}>
                        <Button variant="secondary" size="lg" block onClick={() => setShowAddModal(true)}>
                            {t('flash.vote.addCandidate')}
                        </Button>
                    </div>
                )}
            </Section>

            {/* 정해졌으면 모임으로 */}
            {data.status === 'DECIDED' && data.decidedMeetingId && (
                <Section divided last>
                    <Button
                        variant="brand"
                        size="lg"
                        block
                        onClick={() => navigate(`/meeting/detail/${data.decidedMeetingId}`)}
                    >
                        {t('flash.vote.decidedBanner')}
                    </Button>
                </Section>
            )}

            {/* 이름 묻기 — 처음 투표할 때만 */}
            {showNameModal && (
                <div className={styles.overlay} onClick={() => setShowNameModal(false)}>
                    <div
                        className={styles.sheet}
                        role="dialog"
                        aria-modal="true"
                        aria-label={t('flash.vote.nameModalTitle')}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className={styles.sheetHead}>
                            <h2 className={styles.sheetTitle}>{t('flash.vote.nameModalTitle')}</h2>
                        </div>
                        <div className={styles.sheetBody}>
                            <p className={styles.sheetSub}>{t('flash.vote.nameModalSub')}</p>
                            <TextField
                                id="voter-name"
                                label={t('guestLogin.name')}
                                type="text"
                                autoFocus
                                value={nameInput}
                                onChange={(e) => setNameInput(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') handleConfirmName(); }}
                                placeholder={t('flash.vote.namePlaceholder')}
                                maxLength={20}
                            />
                            <div className={styles.sheetAction}>
                                <Button
                                    variant="primary"
                                    size="lg"
                                    block
                                    onClick={handleConfirmName}
                                    disabled={!nameInput.trim()}
                                >
                                    {t('flash.vote.joinBtn')}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* 후보 제안 — 모임 장소 추가와 같은 고르기 창 */}
            <PlacePickerModal
                open={showAddModal}
                onClose={() => setShowAddModal(false)}
                onSelect={async (place) => {
                    if (!token || isAddingPlace) return;
                    setIsAddingPlace(true);
                    try {
                        const res: any = await FlashRepository.onPostAddCandidate(token, {
                            placeName: place.name,
                            placeAddress: place.address,
                            latitude: place.lat,
                            longitude: place.lng,
                            externalPlaceId: place.id,
                            imageUrl: place.imageUrl,
                        });
                        const status = res?.status ?? 0;
                        if (status >= 200 && status < 300) {
                            setToastMsg(t('flash.vote.candidateAdded'));
                            await load();
                        } else {
                            setToastMsg(res?.data?.message || t('flash.vote.candidateAddFailed'));
                        }
                    } catch {
                        setToastMsg(t('flash.vote.candidateAddFailed'));
                    } finally {
                        setIsAddingPlace(false);
                        setTimeout(() => setToastMsg(''), 2000);
                    }
                }}
                title={t('flash.suggestCandidate')}
                confirmButtonText={t('flash.vote.proposeThisPlace')}
            />

            {toastMsg && <div className={styles.toast}>{toastMsg}</div>}
        </AppScreen>
    );
};
